import { MediumFormRow, BigFormRow } from 'framework/ui/layout';
import { Modal } from 'framework/ui/ui-builder';
import { ModalForm, FormFieldsData, DEFAULT_ERROR_MESSAGE, TextInput } from 'framework/form';
import { getFileTypeFromName } from 'domain/shared/artifact';
import { MAX_SIZE } from 'domain/shared/artifact/input';
import { ArtifactStorageUploaderInput } from 'domain/shared/artifactStorage';
import { useDocumentStorageContext } from 'framework/documentStorage';
import {
  ACCEPTANCE_ARTIFACT_MODAL_MODE,
  ACCEPTANCE_DOCUMENT_FILE_TYPE,
  type AcceptanceArtifactModalMode,
} from '../../constants';
import type { AcceptanceDocumentSearchResultItem } from '../../acceptanceTypes';
import { useShowContext } from 'framework/show';
import { acceptanceArtifactUploadValidation, editArtifactValidation } from '../../acceptanceValidation';
import { useProjectDataContext } from 'domain/shared/newProject';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';

type AcceptanceArtifactModalProps = {
  mode: AcceptanceArtifactModalMode;
  document?: AcceptanceDocumentSearchResultItem | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

const MODAL_TITLES: Record<AcceptanceArtifactModalMode, string> = {
  [ACCEPTANCE_ARTIFACT_MODAL_MODE.CREATE]: 'Add acceptance artifact',
  [ACCEPTANCE_ARTIFACT_MODAL_MODE.EDIT]: 'Edit acceptance artifact',
};

const reportError = (addMessage: ReturnType<typeof useMessageService>['addMessage']) => {
  addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
  throw new Error();
};

type SubmitValues = {
  artifactId: string[];
  currentPath?: string;
  label?: string;
};

const AcceptanceArtifactModal = ({ mode, isOpen, onClose, onSuccess }: AcceptanceArtifactModalProps) => {
  const { addMessage } = useMessageService();
  const { uploadDocument, editDocument } = useDocumentStorageContext();
  const { data: projectData } = useProjectDataContext();
  const { getUserAlias } = useAuthenticatedUserService();
  const { data: showData } = useShowContext();
  const rowData = showData.getData();
  const submitFormFunc = async (values: SubmitValues) => {
    const artifactIds = values.artifactId ?? [];
    const previousPath = values.currentPath;
    const isEdit = mode === ACCEPTANCE_ARTIFACT_MODAL_MODE.EDIT;

    if (isEdit && previousPath) {
      try {
        await editDocument(values as FormFieldsData);
        return {};
      } catch {
        reportError(addMessage);
      }
    }

    if (artifactIds.length === 0) return;

    try {
      const projectId = projectData?.id;
      const path = artifactIds[0];
      if (!projectId || !path) return;
      const updatedBy = (await getUserAlias()) ?? '';
      const payload = {
        projectId,
        paths: [path],
        fileType: ACCEPTANCE_DOCUMENT_FILE_TYPE,
        metadata: {
          label: (values.label as string) || undefined,
          updatedBy,
          type: getFileTypeFromName(path),
        },
      };
      await uploadDocument(payload);
      return {};
    } catch {
      reportError(addMessage);
    }
  };
  return (
    <Modal
      title={MODAL_TITLES[mode]}
      isOpen={isOpen}
      onToggle={onClose}
      hasCancelButton
      modalSize="sm"
      paperStyles={{ backgroundColor: 'var(--mui-palette-background-paper)' }}
      backdrop="static"
    >
      <ModalForm
        closeModal={onClose}
        modalOpen={isOpen}
        getValidationSchemaFunc={() =>
          Promise.resolve(
            mode === ACCEPTANCE_ARTIFACT_MODAL_MODE.CREATE
              ? acceptanceArtifactUploadValidation
              : editArtifactValidation,
          )
        }
        submitFormFunc={async (values: FormFieldsData) => submitFormFunc(values as SubmitValues)}
        afterSubmit={{
          message: 'Acceptance artifact has been updated.',
          execute: () => {
            onSuccess?.();
            onClose();
          },
        }}
        initialData={{
          label: rowData?.metadata?.label ?? '',
          artifactId: rowData?.path ? [rowData.path] : [],
          currentPath: rowData?.path,
        }}
      >
        <MediumFormRow>
          <TextInput source="label" label="Name" placeholder="Optional" />
        </MediumFormRow>
        <BigFormRow>
          <ArtifactStorageUploaderInput
            source="artifactId"
            label="File"
            isRequired={mode === ACCEPTANCE_ARTIFACT_MODAL_MODE.CREATE}
            maxElements={1}
            maximumSize={MAX_SIZE}
            isNewDocumentStorage
          />
        </BigFormRow>
      </ModalForm>
    </Modal>
  );
};

export default AcceptanceArtifactModal;
